import { useMemo } from "react";
import { AlertTriangle, CalendarClock, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  PINNED_OS_FLOOR,
  PinnedConfigState,
  SU_PLATFORM_META,
  SoftwareUpdateWizardState,
} from "../types";
import { isValidDeadline, isValidTargetVersion, normalizeTargetLocalDateTime } from "../lib/buildConfig";

interface Props {
  state: SoftwareUpdateWizardState;
  onChange: (patch: Partial<PinnedConfigState>) => void;
}

export function PinnedForm({ state, onChange }: Props) {
  const p = state.pinned;
  const floor = PINNED_OS_FLOOR[state.platform];
  const platformLabel = SU_PLATFORM_META[state.platform].label;

  const versionError = useMemo(
    () => (p.targetVersion && !isValidTargetVersion(p.targetVersion) ? "Use a version like 15.5 or 15.5.1." : ""),
    [p.targetVersion],
  );

  const deadlineError = useMemo(
    () => (p.deadline && !isValidDeadline(p.deadline) ? "Use a date and time without timezone." : ""),
    [p.deadline],
  );

  const urlError = useMemo(
    () => (p.detailsUrl && !/^https:\/\//i.test(p.detailsUrl.trim()) ? "URL must start with https://" : ""),
    [p.detailsUrl],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pinned version</CardTitle>
        <CardDescription>
          Build <code className="font-mono text-xs">com.apple.configuration.softwareupdate.enforcement.specific</code>.
          Devices already past the target are left alone.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-2">
          <Label htmlFor="pinned-target">
            Target version <span className="text-destructive">*</span>
          </Label>
          <Input
            id="pinned-target"
            value={p.targetVersion}
            placeholder="e.g. 15.5"
            inputMode="decimal"
            onChange={(e) => onChange({ targetVersion: e.target.value })}
            aria-invalid={!!versionError}
          />
          <p className={`text-xs ${versionError ? "text-destructive" : "text-muted-foreground"}`}>
            {versionError || `Plain version string. ${platformLabel} floor: ${floor ?? "n/a"}.`}
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="pinned-build">Target build (optional)</Label>
          <Input
            id="pinned-build"
            value={p.targetBuild}
            placeholder="e.g. 24F74"
            onChange={(e) => onChange({ targetBuild: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Used during seeding — may carry a supplemental letter (e.g. <code>20A242a</code>).
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="pinned-deadline" className="flex items-center gap-1">
            <CalendarClock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Enforcement deadline <span className="text-destructive">*</span>
            <span className="text-xs font-normal text-muted-foreground">(device local time)</span>
          </Label>
          <Input
            id="pinned-deadline"
            type="datetime-local"
            step={1}
            value={p.deadline}
            onChange={(e) => onChange({ deadline: e.target.value })}
            aria-invalid={!!deadlineError}
          />
          <p className={`text-xs ${deadlineError ? "text-destructive" : "text-muted-foreground"} flex items-start gap-1`}>
            <Info className="h-3 w-3 mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              {deadlineError ||
                `Will be sent as ${normalizeTargetLocalDateTime(p.deadline) || "YYYY-MM-DDTHH:MM:SS"} — no timezone offset.`}
            </span>
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="pinned-url">Details URL (optional)</Label>
          <Input
            id="pinned-url"
            type="url"
            value={p.detailsUrl}
            placeholder="https://your-org.example.com/patching/abc123"
            onChange={(e) => onChange({ detailsUrl: e.target.value })}
            aria-invalid={!!urlError}
          />
          <p className={`text-xs ${urlError ? "text-destructive" : "text-muted-foreground"}`}>
            {urlError || "Shown to users in update prompts. HTTPS only."}
          </p>
        </div>

        {floor && (
          <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-xs">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5" aria-hidden="true" />
            <div>
              <div className="font-medium text-foreground">Supported OS floor</div>
              <div className="text-muted-foreground">
                Pinned enforcement requires {platformLabel} {floor}+. Devices below the floor will ignore the policy.
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
